import type { EmailTemplateKind } from './email-templates.js';

// ---------------------------------------------------------------------------
// Per-letter registry — drives the admin editor UI and the preview.
//
// Marketers experience "letters" (what lands in an inbox), not template
// "kinds" (the technical rendering unit). Several letters share a kind
// (approval_topics + approval_content both render via `approval_link`,
// with stage-specific fields living side by side on that kind), and one
// letter (client_feedback) fans out into per-decision variants. This file
// is the single source of truth mapping letters onto kinds/fields so the
// editor never needs to know about kinds directly.
// ---------------------------------------------------------------------------

export type EmailLetterGroup = 'client' | 'internal';
export type EmailLetterInput = 'line' | 'rich';

export interface EmailLetterField {
  name: string;
  label: string;
  input: EmailLetterInput;
  sharedNote?: string;
}

export interface EmailLetterVariant {
  id: string;
  label: string;
  fields: EmailLetterField[];
}

export interface EmailLetterDescriptor {
  id: string;
  title: string;
  description: string;
  group: EmailLetterGroup;
  kind: EmailTemplateKind;
  fields: EmailLetterField[];
  variants?: EmailLetterVariant[];
  previewSamples?: { id: string; label: string }[];
  allowedVariables: string[];
}

const SHARED_APPROVAL_NOTE = 'A két jóváhagyó levél közös eleme.';

export const EMAIL_LETTERS: EmailLetterDescriptor[] = [
  {
    id: 'approval_topics',
    title: 'Témajavaslatok jóváhagyása',
    description: 'Az ügyfél linket kap a következő időszak témáinak átnézéséhez.',
    group: 'client',
    kind: 'approval_link',
    fields: [
      { name: 'subjectTopics', label: 'Tárgy', input: 'line' },
      {
        name: 'brandingHeader',
        label: 'Fejléc',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      { name: 'headlineTopics', label: 'Főcím', input: 'rich' },
      { name: 'introTopics', label: 'Bevezető', input: 'rich' },
      {
        name: 'buttonLabel',
        label: 'Gomb felirata',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'linkHelperText',
        label: 'Gomb-segédszöveg',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'footer',
        label: 'Lábléc',
        input: 'rich',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'signOff',
        label: 'Elköszönés',
        input: 'rich',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
    ],
    allowedVariables: ['{{hotelName}}'],
  },
  {
    id: 'approval_content',
    title: 'Posztok végső jóváhagyása',
    description: 'Az ügyfél linket kap a kész posztok véglegesítéséhez, mielőtt közzétesszük őket.',
    group: 'client',
    kind: 'approval_link',
    fields: [
      { name: 'subjectContent', label: 'Tárgy', input: 'line' },
      {
        name: 'brandingHeader',
        label: 'Fejléc',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      { name: 'headlineContent', label: 'Főcím', input: 'rich' },
      { name: 'introContent', label: 'Bevezető', input: 'rich' },
      {
        name: 'buttonLabel',
        label: 'Gomb felirata',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'linkHelperText',
        label: 'Gomb-segédszöveg',
        input: 'line',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'footer',
        label: 'Lábléc',
        input: 'rich',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
      {
        name: 'signOff',
        label: 'Elköszönés',
        input: 'rich',
        sharedNote: SHARED_APPROVAL_NOTE,
      },
    ],
    allowedVariables: ['{{hotelName}}'],
  },
  {
    id: 'approval_summary',
    title: 'Csomag-összefoglaló a döntések után',
    description:
      'Az ügyfél ezt kapja, miután végigment egy beküldött csomagon: összesíti, mely posztokat hagyta jóvá és melyeket utasította el.',
    group: 'client',
    kind: 'approval_summary',
    fields: [
      { name: 'subjectPrefix', label: 'Tárgy előtag', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'headline', label: 'Főcím', input: 'rich' },
      { name: 'intro', label: 'Bevezető', input: 'rich' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
      { name: 'signOff', label: 'Elköszönés', input: 'rich' },
    ],
    allowedVariables: ['{{hotelName}}', '{{periodLabel}}'],
  },
  {
    id: 'client_feedback',
    title: 'Visszaigazoló egy posztról',
    description:
      'Az ügyfél kapja, amikor egy kolléga a nevében döntést rögzít, és kéri az értesítését.',
    group: 'client',
    kind: 'client_feedback',
    fields: [
      { name: 'subject', label: 'Tárgy', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
      { name: 'signOff', label: 'Elköszönés', input: 'rich' },
    ],
    variants: [
      {
        id: 'approve',
        label: 'Jóváhagyta',
        fields: [
          { name: 'headlineApprove', label: 'Főcím', input: 'rich' },
          { name: 'introApprove', label: 'Bevezető', input: 'rich' },
        ],
      },
      {
        id: 'request_changes',
        label: 'Módosítást kért',
        fields: [
          { name: 'headlineChanges', label: 'Főcím', input: 'rich' },
          { name: 'introChanges', label: 'Bevezető', input: 'rich' },
        ],
      },
      {
        id: 'reject',
        label: 'Elvetette',
        fields: [
          { name: 'headlineReject', label: 'Főcím', input: 'rich' },
          { name: 'introReject', label: 'Bevezető', input: 'rich' },
        ],
      },
    ],
    allowedVariables: ['{{hotelName}}', '{{periodLabel}}', '{{postTitle}}'],
  },
  {
    id: 'approval_chase',
    title: 'Emlékeztető nyitott jóváhagyásra',
    description: 'Emlékeztető megy az ügyfélnek, ha egy jóváhagyás régóta nyitva áll.',
    group: 'client',
    kind: 'approval_chase',
    fields: [
      { name: 'subject', label: 'Tárgy', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'headline', label: 'Főcím', input: 'rich' },
      { name: 'intro', label: 'Bevezető', input: 'rich' },
      { name: 'ctaLabel', label: 'Gomb felirata', input: 'line' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg', input: 'line' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
      { name: 'signOff', label: 'Elköszönés', input: 'rich' },
    ],
    allowedVariables: ['{{hotelName}}', '{{requestDate}}'],
  },
  {
    id: 'client_reply',
    title: 'Válasz az ügyfél megjegyzésére',
    description:
      'Az ügyfél ezt kapja, ha a csapat válaszolt a posztnál hagyott megjegyzésére; a válaszok egy levélben mennek.',
    group: 'client',
    kind: 'client_reply',
    fields: [
      { name: 'subject', label: 'Tárgy', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'headline', label: 'Főcím', input: 'rich' },
      { name: 'intro', label: 'Bevezető', input: 'rich' },
      { name: 'ctaLabel', label: 'Gomb felirata', input: 'line' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
      { name: 'signOff', label: 'Elköszönés', input: 'rich' },
    ],
    allowedVariables: ['{{hotelName}}'],
  },
  {
    id: 'magic_link',
    title: 'Bejelentkező link',
    description: 'A felhasználó bejelentkező linket kap, amikor jelszó nélkül szeretne belépni.',
    group: 'internal',
    kind: 'magic_link',
    fields: [
      { name: 'subject', label: 'Tárgy', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'greeting', label: 'Megszólítás', input: 'rich' },
      { name: 'headline', label: 'Főcím', input: 'rich' },
      { name: 'intro', label: 'Bevezető', input: 'rich' },
      { name: 'buttonLabel', label: 'Gomb felirata', input: 'line' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg', input: 'line' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
    ],
    allowedVariables: ['{{recipientName}}', '{{expiresInMinutes}}'],
  },
  {
    id: 'notification_internal',
    title: 'Belső értesítő / emlékeztető',
    description:
      'A Morgens-munkatársak esemény-értesítői; a törzs az eseményből jön, itt a keret szabható.',
    group: 'internal',
    kind: 'notification',
    fields: [
      { name: 'subjectPrefix', label: 'Tárgy előtag', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'ctaLabel', label: 'Gomb felirata', input: 'line' },
      { name: 'linkHelperText', label: 'Gomb-segédszöveg', input: 'line' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
      { name: 'signOff', label: 'Elköszönés (csak ügyfélnek)', input: 'rich' },
    ],
    previewSamples: [
      { id: 'client_decision', label: 'Esemény-értesítő (ügyfél döntött)' },
      { id: 'stale_approval', label: 'Régóta nyitott jóváhagyás' },
    ],
    allowedVariables: [],
  },
  {
    id: 'notification_digest',
    title: 'Napi összefoglaló',
    description:
      'Napi összefoglaló megy a Morgens-munkatársaknak a felgyűlt eseményekről, a saját beállított órájukban.',
    group: 'internal',
    kind: 'notification_digest',
    fields: [
      { name: 'subject', label: 'Tárgy', input: 'line' },
      { name: 'brandingHeader', label: 'Fejléc', input: 'line' },
      { name: 'intro', label: 'Bevezető', input: 'rich' },
      { name: 'footer', label: 'Lábléc', input: 'rich' },
    ],
    allowedVariables: ['{{count}}', '{{recipientName}}'],
  },
];

export function getEmailLetter(id: string): EmailLetterDescriptor | undefined {
  return EMAIL_LETTERS.find((l) => l.id === id);
}

/**
 * Field names a caller may legitimately submit for this letter — its own
 * fields plus every variant's fields (client_feedback's per-decision
 * headline/intro). Used to reject a preview/update request that tries to
 * smuggle an unrelated field name (e.g. a different letter's field, or a
 * raw kind-level field the letter doesn't expose) into this letter's slot.
 */
export function getEmailLetterAllowedFieldNames(letter: EmailLetterDescriptor): string[] {
  const names = new Set(letter.fields.map((f) => f.name));
  for (const variant of letter.variants ?? []) {
    for (const f of variant.fields) names.add(f.name);
  }
  return [...names];
}

const RICH_BY_KIND = new Map<string, Set<string>>();
for (const letter of EMAIL_LETTERS) {
  const set = RICH_BY_KIND.get(letter.kind) ?? new Set<string>();
  for (const f of [...letter.fields, ...(letter.variants ?? []).flatMap((v) => v.fields)]) {
    if (f.input === 'rich') set.add(f.name);
  }
  RICH_BY_KIND.set(letter.kind, set);
}

export function isRichField(kind: EmailTemplateKind, fieldName: string): boolean {
  return RICH_BY_KIND.get(kind)?.has(fieldName) ?? false;
}
